import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Trans, useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { ColumnDef } from '@tanstack/react-table';
import { Download, Mail, MoreHorizontal, Pencil, Trash2 } from 'lucide-react';
import { apiService } from '../services/api.service';
import { exportTable, TableExportData } from '../lib/tableExport';
import { cn } from '../lib/utils';
import { useEvent } from '../contexts/EventContext';
import { Button } from '../components/ui/button';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Toolbar } from '../components/ui/page-header';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import ConfirmDialog from '../components/ui/ConfirmDialog';
import { ExportMenu } from '../components/ExportMenu';
import { DataTable } from '../components/data-table/DataTable';
import { BulkAction, BulkActionBar } from '../components/data-table/BulkActionBar';
import { FilterChip } from '../components/data-table/FilterChip';
import { SearchField } from '../components/data-table/SearchField';
import { VolunteerSheet, VolunteerPatch } from '../components/volunteers/VolunteerSheet';
import { joursDe, libelleDisponibilite } from '../components/volunteers/disponibilite';
import {
  ROLES, Volunteer, VolunteerRole, formatAnswer, useVolunteerForm, useVolunteers, volunteersQueryKey,
} from '../hooks/useVolunteers';

// Bénévoles (refonte 2026-09-27) : la grammaire de la page Équipes.
//  - recherche sur nom, email, rôle, disponibilité et TOUTES les réponses ;
//  - tri sur chaque colonne, une colonne par question du formulaire ;
//  - filtres en étiquettes : rôle, jour de présence, et chaque question à choix ;
//  - sélection multiple → message, rôle en lot, export, retrait ;
//  - édition en panneau latéral, qui édite tout (rôle et créneaux compris).

const NO_ROLE = '__none';
const NO_AVAILABILITY = '__unknown';

interface Props {
  eventId: string;
  embedded?: boolean;
}

export const VolunteerResponsesContent: React.FC<Props> = ({ eventId, embedded = false }) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { events } = useEvent();
  const eventName = events.find((e) => e.id === eventId)?.name;

  const volunteersQuery = useVolunteers(eventId);
  const formQuery = useVolunteerForm(eventId);
  const volunteers = useMemo(() => volunteersQuery.data ?? [], [volunteersQuery.data]);
  const definition = formQuery.data ?? null;
  const freeFields = useMemo(() => definition?.freeFields ?? [], [definition]);

  const yes = t('volunteers.list.yes');
  const no = t('volunteers.list.no');
  const roleLabel = useCallback((r: string | null) => (r ? t(`volunteers.role.${r}`) : t('volunteers.list.noRole')), [t]);
  const slotsLabel = useCallback((v: Volunteer) =>
    libelleDisponibilite(v.available_slots, i18n.language), [i18n.language]);
  const dayLabel = useCallback((d: string) =>
    new Date(`${d}T12:00:00`).toLocaleDateString(i18n.language, { weekday: 'short', day: 'numeric', month: 'short' }), [i18n.language]);

  // Filtres
  const [query, setQuery] = useState('');
  const [roleFilter, setRoleFilter] = useState<string[]>([]);
  const [dayFilter, setDayFilter] = useState<string[]>([]);
  const [answerFilters, setAnswerFilters] = useState<Record<string, string[]>>({});
  const days = useMemo(() => joursDe(volunteers.flatMap((v) => v.available_slots ?? [])), [volunteers]);
  const choiceFields = useMemo(() => freeFields.filter((f) => (f.options?.length ?? 0) > 0), [freeFields]);

  const answerValues = (v: Volunteer, fieldId: string): string[] => {
    const raw = v.answers?.[fieldId];
    return Array.isArray(raw) ? raw.map(String) : raw ? [String(raw)] : [];
  };

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return volunteers.filter((v) => {
      if (roleFilter.length && !roleFilter.includes(v.role ?? NO_ROLE)) return false;
      if (dayFilter.length) {
        const present = v.available_slots?.length ? joursDe(v.available_slots) : [NO_AVAILABILITY];
        if (!dayFilter.some((d) => present.includes(d))) return false;
      }
      for (const [fieldId, wanted] of Object.entries(answerFilters)) {
        if (wanted.length && !answerValues(v, fieldId).some((a) => wanted.includes(a))) return false;
      }
      if (!q) return true;
      return [
        v.first_name, v.last_name, v.email, roleLabel(v.role), slotsLabel(v),
        ...Object.values(v.answers ?? {}).map((a) => formatAnswer(a, yes, no)),
      ].filter(Boolean).join(' ').toLowerCase().includes(q);
    });
  }, [volunteers, query, roleFilter, dayFilter, answerFilters, roleLabel, slotsLabel, yes, no]);

  // Sélection
  const [selection, setSelection] = useState<Record<string, boolean>>({});
  const selectedIds = Object.keys(selection).filter((id) => selection[id]);
  const selected = volunteers.filter((v) => selection[v.id]);
  useEffect(() => { setSelection({}); }, [eventId]);

  // Panneau
  // Le panneau écrit à chaque champ : rien n'attend jamais, donc changer de
  // bénévole n'a rien à protéger.
  const [sheetId, setSheetId] = useState<string | null>(null);
  const sheetVolunteer = volunteers.find((v) => v.id === sheetId) ?? null;
  const indexInView = filtered.findIndex((v) => v.id === sheetId);
  const openVolunteer = (id: string) => setSheetId(id);
  const closeSheet = () => { setSheetId(null); setServerError(''); };

  const [serverError, setServerError] = useState('');
  const refresh = () => qc.invalidateQueries({ queryKey: volunteersQueryKey(eventId) });

  // L'envoi est PARTIEL : le panneau écrit chaque champ dès qu'on le quitte,
  // le serveur préserve le reste. Le rôle part tel quel ; la disponibilité
  // part par la réponse au formulaire, dont le serveur déduit les créneaux.
  const patchVolunteer = async (patch: VolunteerPatch) => {
    if (!sheetVolunteer) return;
    await apiService.put(`/events/${eventId}/volunteers/${sheetVolunteer.id}`, patch);
    refresh();
  };

  // Rôle en lot : une écriture par bénévole, et un échec partiel se dit en
  // nommant ceux qui n'ont pas bougé, jamais en silence.
  const [bulkBusy, setBulkBusy] = useState(false);
  const setRoleBulk = async (targets: Volunteer[], role: VolunteerRole) => {
    setBulkBusy(true);
    const failed: string[] = [];
    for (const v of targets) {
      try { await apiService.put(`/events/${eventId}/volunteers/${v.id}`, { role }); } catch { failed.push(`${v.first_name} ${v.last_name}`); }
    }
    setBulkBusy(false);
    setSelection({});
    refresh();
    if (failed.length) toast.error(t('volunteers.list.failed', { names: failed.join(', ') }));
    else toast.success(t('volunteers.list.roleUpdated', { count: targets.length, role: roleLabel(role) }));
  };

  // Retrait — définitif côté serveur : il se confirme.
  const [toRemove, setToRemove] = useState<Volunteer[]>([]);
  const [removing, setRemoving] = useState(false);
  const confirmRemove = async () => {
    setRemoving(true);
    const failed: string[] = [];
    for (const v of toRemove) {
      try { await apiService.delete(`/events/${eventId}/volunteers/${v.id}`); } catch { failed.push(`${v.first_name} ${v.last_name}`); }
    }
    setRemoving(false);
    setToRemove([]);
    setSelection({});
    if (toRemove.some((v) => v.id === sheetId)) closeSheet();
    refresh();
    if (failed.length) toast.error(t('volunteers.list.failed', { names: failed.join(', ') }));
    else if (toRemove.length === 1) toast.success(t('volunteers.remove.done', { name: `${toRemove[0].first_name} ${toRemove[0].last_name}` }));
    else toast.success(t('volunteers.list.removedMany', { count: toRemove.length }));
  };

  const exportData = (rows: Volunteer[]): TableExportData => ({
    filename: `benevoles-${eventName || eventId}`,
    title: t('volunteers.title'),
    subtitle: eventName,
    headers: [
      t('volunteers.colName'), t('volunteers.colEmail'), t('volunteers.colRole'), t('volunteers.colDays'),
      ...freeFields.map((f) => f.label), t('volunteers.colRegisteredAt'),
    ],
    rows: rows.map((v) => [
      `${v.first_name} ${v.last_name}`, v.email, v.role ? roleLabel(v.role) : '', slotsLabel(v),
      ...freeFields.map((f) => formatAnswer(v.answers?.[f.id], yes, no)),
      new Date(v.created_at).toLocaleDateString(i18n.language),
    ]),
  });

  const columns = useMemo<ColumnDef<Volunteer, any>[]>(() => [
    {
      id: 'name',
      // Tri sur ce que la colonne AFFICHE (Prénom Nom) : trié par nom de famille
      // sous une colonne qui montre le prénom en premier, l'ordre paraissait
      // aléatoire (constat de Bryan sur staging, 2026-09-27).
      accessorFn: (v) => `${v.first_name} ${v.last_name}`,
      header: () => t('volunteers.colName'),
      sortingFn: (a, b) => `${a.original.first_name} ${a.original.last_name}`.localeCompare(`${b.original.first_name} ${b.original.last_name}`, undefined, { sensitivity: 'base' }),
      cell: ({ row }) => (
        <div className="min-w-0">
          <div className="font-semibold text-foreground">{row.original.first_name} {row.original.last_name}</div>
          <div className="mt-0.5 truncate text-xs text-muted-foreground">{row.original.email}</div>
        </div>
      ),
    },
    {
      id: 'role',
      accessorFn: (v) => roleLabel(v.role),
      header: () => t('volunteers.colRole'),
      cell: ({ row }) => (
        <span className={cn(row.original.role ? 'text-foreground' : 'text-muted-foreground')}>{roleLabel(row.original.role)}</span>
      ),
    },
    {
      id: 'availability',
      accessorFn: (v) => (v.available_slots?.length ? joursDe(v.available_slots)[0] + (v.available_slots[0]?.start ?? '') : 'zzz'),
      header: () => t('volunteers.colDays'),
      cell: ({ row }) => {
        const label = slotsLabel(row.original);
        return label
          ? <span className="whitespace-nowrap text-sm text-muted-foreground">{label}</span>
          : <span className="text-sm text-warning">{t('volunteers.list.unknownAvailability')}</span>;
      },
    },
    ...freeFields.map<ColumnDef<Volunteer, any>>((f) => ({
      id: `answer-${f.id}`,
      accessorFn: (v) => formatAnswer(v.answers?.[f.id], yes, no),
      header: () => f.label,
      cell: ({ getValue }) => <span className="text-muted-foreground">{getValue()}</span>,
    })),
    {
      id: 'created_at',
      accessorFn: (v) => v.created_at,
      header: () => t('volunteers.colRegisteredAt'),
      cell: ({ row }) => (
        <span className="whitespace-nowrap text-muted-foreground">
          {new Date(row.original.created_at).toLocaleDateString(i18n.language, { day: 'numeric', month: 'short' })}
        </span>
      ),
    },
    {
      id: 'actions',
      enableSorting: false,
      header: () => <span className="sr-only">{t('common.actions')}</span>,
      cell: ({ row }) => {
        const v = row.original;
        return (
          <div className="flex justify-end" onClick={(e) => e.stopPropagation()}>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground" aria-label={t('volunteers.list.rowActions', { name: `${v.first_name} ${v.last_name}` })}>
                  <MoreHorizontal size={16} />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuItem onClick={() => openVolunteer(v.id)}><Pencil size={16} className="mr-2" />{t('common.edit')}</DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate(`/admin/messages?volunteers=${v.id}`)}><Mail size={16} className="mr-2" />{t('volunteers.list.sendMessage')}</DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem className="text-destructive focus:text-destructive" onClick={() => setToRemove([v])}>
                  <Trash2 size={16} className="mr-2" />{t('volunteers.remove.action')}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        );
      },
    },
  // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [t, i18n.language, roleLabel, slotsLabel, freeFields, yes, no, navigate]);

  const filtersActive = roleFilter.length + dayFilter.length + Object.values(answerFilters).flat().length > 0 || !!query;
  const judges = volunteers.filter((v) => v.role === 'judge').length;
  const unknownAvailability = volunteers.filter((v) => !v.available_slots?.length).length;

  return (
    <div className={cn(embedded ? 'space-y-4' : 'space-y-4 p-6', sheetVolunteer && 'min-[1600px]:pr-[584px]')}>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <span className="text-sm text-muted-foreground">
          {volunteersQuery.isLoading ? '' : [
            t('volunteers.list.count', { count: volunteers.length }),
            judges ? t('volunteers.list.judgesCount', { count: judges }) : null,
            unknownAvailability ? t('volunteers.list.noAvailabilityCount', { count: unknownAvailability }) : null,
          ].filter(Boolean).join(' · ')}
        </span>
        <ExportMenu getData={() => exportData(filtered)} disabled={!volunteers.length} />
      </div>

      <Toolbar>
        <SearchField value={query} onChange={setQuery} placeholder={t('volunteers.list.searchPlaceholder')} />
        <FilterChip
          label={t('volunteers.colRole')}
          selected={roleFilter}
          onChange={setRoleFilter}
          options={[...ROLES, NO_ROLE]
            .map((r) => ({ value: r, label: roleLabel(r === NO_ROLE ? null : r), count: volunteers.filter((v) => (v.role ?? NO_ROLE) === r).length }))
            .filter((o) => o.count > 0)}
        />
        {(days.length > 0 || unknownAvailability > 0) && (
          <FilterChip
            label={t('volunteers.colDays')}
            selected={dayFilter}
            onChange={setDayFilter}
            options={[
              ...days.map((d) => ({ value: d, label: dayLabel(d), count: volunteers.filter((v) => joursDe(v.available_slots).includes(d)).length })),
              ...(unknownAvailability ? [{ value: NO_AVAILABILITY, label: t('volunteers.list.unknownAvailability'), count: unknownAvailability }] : []),
            ]}
          />
        )}
        {choiceFields.map((f) => (
          <FilterChip
            key={f.id}
            label={f.label}
            selected={answerFilters[f.id] ?? []}
            onChange={(next) => setAnswerFilters((prev) => ({ ...prev, [f.id]: next }))}
            options={(f.options || []).map((opt) => ({ value: opt, label: opt, count: volunteers.filter((v) => answerValues(v, f.id).includes(opt)).length }))}
          />
        ))}
        {filtersActive && (
          <span className="text-xs text-muted-foreground">{t('volunteers.list.filteredCount', { shown: filtered.length, total: volunteers.length })}</span>
        )}
      </Toolbar>

      {(volunteersQuery.isError || formQuery.isError) && (
        <Alert variant="destructive">
          <AlertDescription className="flex items-center justify-between gap-4">
            {t('volunteers.loadError')}
            <Button variant="outline" size="sm" onClick={() => { volunteersQuery.refetch(); formQuery.refetch(); }}>{t('common.retry')}</Button>
          </AlertDescription>
        </Alert>
      )}

      <DataTable
        ariaLabel={t('volunteers.title')}
        columns={columns}
        data={filtered}
        getRowId={(v) => v.id}
        loading={volunteersQuery.isLoading || formQuery.isLoading}
        selectable
        rowSelection={selection}
        onRowSelectionChange={setSelection}
        onRowClick={(v) => openVolunteer(v.id)}
        activeRowId={sheetId}
        initialSorting={[{ id: 'name', desc: false }]}
        empty={volunteers.length === 0 ? t('volunteers.empty') : t('volunteers.list.noMatch')}
        renderCard={(v, { selected: isSelected, toggle }) => (
          <div className="flex items-start gap-3 py-3">
            <input type="checkbox" checked={isSelected} onChange={toggle} onClick={(e) => e.stopPropagation()} aria-label={t('dataTable.selectRow')} className="mt-1 h-4 w-4 accent-white" />
            <div className="min-w-0 flex-1">
              <div className="truncate font-semibold">{v.first_name} {v.last_name}</div>
              <div className="truncate text-xs text-muted-foreground">{v.email}</div>
              <div className="mt-1.5 flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
                <span>{roleLabel(v.role)}</span>
                {slotsLabel(v) ? <span>{slotsLabel(v)}</span> : <span className="text-warning">{t('volunteers.list.unknownAvailability')}</span>}
              </div>
            </div>
          </div>
        )}
      />

      <BulkActionBar count={selectedIds.length} onClear={() => setSelection({})}>
        <BulkAction onClick={() => navigate(`/admin/messages?volunteers=${selectedIds.join(',')}`)}><Mail />{t('volunteers.list.sendMessage')}</BulkAction>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <BulkAction disabled={bulkBusy}>{t('volunteers.list.setRole')} ▾</BulkAction>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="center" side="top">
            {ROLES.map((r) => <DropdownMenuItem key={r} onClick={() => setRoleBulk(selected, r)}>{roleLabel(r)}</DropdownMenuItem>)}
          </DropdownMenuContent>
        </DropdownMenu>
        <BulkAction onClick={() => exportTable('xlsx', exportData(selected)).catch(() => toast.error(t('export.failed')))}>
          <Download />{t('volunteers.list.exportSelection')}
        </BulkAction>
        <BulkAction className="text-destructive" onClick={() => setToRemove(selected)}><Trash2 />{t('volunteers.remove.action')}</BulkAction>
      </BulkActionBar>

      <VolunteerSheet
        open={!!sheetVolunteer}
        volunteer={sheetVolunteer}
        form={definition}
        saving={removing}
        serverError={serverError}
        onPatch={patchVolunteer}
        onRemove={() => { if (sheetVolunteer) setToRemove([sheetVolunteer]); }}
        onClose={closeSheet}
        onPrev={indexInView > 0 ? () => setSheetId(filtered[indexInView - 1].id) : undefined}
        onNext={indexInView >= 0 && indexInView < filtered.length - 1 ? () => setSheetId(filtered[indexInView + 1].id) : undefined}
      />

      <ConfirmDialog
        open={toRemove.length > 0}
        onOpenChange={(o) => { if (!o && !removing) setToRemove([]); }}
        title={t('volunteers.remove.title')}
        message={toRemove.length === 1
          ? <Trans i18nKey="volunteers.remove.confirm" values={{ name: `${toRemove[0].first_name} ${toRemove[0].last_name}` }} components={{ strong: <strong /> }} />
          : t('volunteers.list.removeManyConfirm', { count: toRemove.length })}
        description={t('volunteers.remove.description')}
        confirmLabel={t('volunteers.remove.action')}
        destructive
        loading={removing}
        onConfirm={confirmRemove}
      />
    </div>
  );
};

export default VolunteerResponsesContent;
